import { useRef, useCallback } from "react";

/**
 * A stable callback identity wrapping an always-current implementation.
 *
 * The ref is written during RENDER, not in an effect, so the latest `fn` is already in place
 * before any effect in the same commit runs. The empty dependency array is therefore correct
 * and deliberate.
 *
 * SHARED as of the web-adoption work. It was previously `index.desktop.ts` only, and the
 * docstring explaining that said the quiet part out loud: *"WEB-ONLY, verified by listing
 * apps/mobile/src/hooks — there is no useEvent.ts there."* That is DEMAND-DRIVEN divergence
 * (nobody on mobile had asked yet), not PERMANENT divergence (a platform cannot do this) —
 * the distinction hazard 32b asks to be recorded. Nothing here touches a DOM, a browser API
 * or a bundler feature; it is `useRef` + `useCallback`. So it converges.
 *
 * Behaviour is byte-identical to the web implementation it came from. Both guarantees are
 * pinned by `useEvent.test.ts`: identity stays stable across renders, and the call always
 * reaches the LATEST `fn` rather than the first render's closure.
 */
export function useEvent<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef(fn);

  // Update ref synchronously during render so it's current
  // before any effects run in the same cycle
  ref.current = fn;

  // Return a stable callback that calls ref.current
  return useCallback(
    ((...args: any[]) => {
      return ref.current(...args);
    }) as T,
    []
  );
}
